/**
 * UK postcode utilities for extracting and validating postcode prefixes.
 *
 * UK postcodes follow the format: Outward Code + Inward Code
 * Example: "SW1A 2AA" = "SW1A" (outward) + "2AA" (inward)
 *
 * The Area Code (prefix) is the 1-2 letters at the start:
 * - "SW1A 2AA" → "SW"
 * - "W1A 1AA" → "W"
 * - "EC1A 1BB" → "EC"
 * - "B1 1AA" → "B"
 */

/**
 * Extracts the area code (1-2 letter prefix) from a UK postcode.
 *
 * @param postalCode - A UK postcode string (e.g., "SW1A 2AA", "EC1V 9BD")
 * @returns The 1-2 letter area code in uppercase, or null if invalid
 *
 * @example
 * extractPostcodePrefix("SW1A 2AA") // "SW"
 * extractPostcodePrefix("W1A 1AA")  // "W"
 * extractPostcodePrefix("EC1A 1BB") // "EC"
 * extractPostcodePrefix("B1 1AA")   // "B"
 * extractPostcodePrefix("invalid")  // null
 */
export function extractPostcodePrefix(
  postalCode: string | null | undefined,
): string | null {
  if (!postalCode) {
    return null;
  }

  const normalized = postalCode.toUpperCase().trim();

  // UK postcode area codes are 1-2 letters at the start
  // Match: 1-2 letters followed by at least one digit
  const match = normalized.match(/^([A-Z]{1,2})\d/);

  return match?.[1] ?? null;
}

/**
 * Checks if a postcode's prefix is within a list of allowed prefixes.
 *
 * @param postalCode - A UK postcode string
 * @param allowedPrefixes - Array of allowed postcode prefixes (case-insensitive)
 * @returns true if the postcode prefix is in the allowed list, false otherwise
 *
 * @example
 * isPostcodeInAllowedArea("SW1A 2AA", ["SW", "W", "N"]) // true
 * isPostcodeInAllowedArea("CF10 1AA", ["SW", "W", "N"]) // false
 * isPostcodeInAllowedArea("SW1A 2AA", [])              // true (empty list = all allowed)
 */
export function isPostcodeInAllowedArea(
  postalCode: string,
  allowedPrefixes: string[],
): boolean {
  // Empty list means all areas are allowed
  if (allowedPrefixes.length === 0) {
    return true;
  }

  const prefix = extractPostcodePrefix(postalCode);

  // If we can't extract a prefix, default to allowed
  if (!prefix) {
    return true;
  }

  // Normalize prefixes to uppercase for comparison
  const normalizedAllowed = allowedPrefixes.map((p) => p.toUpperCase().trim());

  return normalizedAllowed.includes(prefix);
}
